import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ExternalLink } from 'lucide-react'

interface TradingViewModalProps {
  isOpen: boolean
  onClose: () => void
  symbol: string
}

function injectWidget(container: HTMLDivElement, src: string, config: Record<string, unknown>) {
  container.innerHTML = ''
  const script = document.createElement('script')
  script.src = src
  script.type = 'text/javascript'
  script.async = true
  script.innerHTML = JSON.stringify(config)
  container.appendChild(script)
}

const TradingViewModal: React.FC<TradingViewModalProps> = ({ isOpen, onClose, symbol }) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const technicalRef = useRef<HTMLDivElement>(null)
  const fundamentalRef = useRef<HTMLDivElement>(null)

  const sym = symbol || 'NASDAQ:AAPL'

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) return

    if (chartRef.current) {
      injectWidget(
        chartRef.current,
        'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js',
        {
          allow_symbol_change: true,
          calendar: false,
          details: false,
          hide_side_toolbar: true,
          hide_top_toolbar: false,
          hide_legend: false,
          hide_volume: false,
          hotlist: false,
          interval: 'D',
          locale: 'en',
          save_image: false,
          style: '1',
          symbol: sym,
          theme: 'dark',
          timezone: 'Asia/Bangkok',
          backgroundColor: '#0F0F0F',
          gridColor: 'rgba(242, 242, 242, 0.06)',
          watchlist: [],
          withdateranges: false,
          compareSymbols: [],
          studies: ['STD;MA%1Cross'],
          autosize: true,
        }
      )
    }

    if (technicalRef.current) {
      injectWidget(
        technicalRef.current,
        'https://s3.tradingview.com/external-embedding/embed-widget-technical-analysis.js',
        {
          symbol: sym,
          interval: '1D',
          showIntervalTabs: true,
          displayMode: 'single',
          locale: 'en',
          colorTheme: 'dark',
          isTransparent: false,
          autosize: true,
        }
      )
    }

    if (fundamentalRef.current) {
      injectWidget(
        fundamentalRef.current,
        'https://s3.tradingview.com/external-embedding/embed-widget-financials.js',
        {
          symbol: sym,
          displayMode: 'regular',
          locale: 'en',
          colorTheme: 'dark',
          isTransparent: false,
          largeChartUrl: '',
          autosize: true,
        }
      )
    }

  }, [isOpen, sym])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="sheet fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center"
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-[#0F0F0F] rounded-2xl w-[95vw] h-[92vh] shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-2.5 flex-shrink-0 border-b border-white/10">
              <h2 className="text-lg font-bold text-white tracking-wide">{sym}</h2>
              <div className="flex items-center gap-2">
                <a
                  href={`https://www.tradingview.com/chart/?symbol=${encodeURIComponent(sym)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs text-black bg-white hover:bg-white/80 transition-colors rounded-lg px-2.5 py-1.5"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open in TradingView
                </a>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors rounded-lg p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Body — 2 columns */}
            <div className="flex-1 min-h-0 flex p-2 gap-2">
              {/* Left 70% — Chart */}
              <div className="flex-[70] min-w-0">
                <div ref={chartRef} className="tradingview-widget-container w-full h-full">
                  <div className="tradingview-widget-container__widget w-full h-full" />
                </div>
              </div>

              <div className="w-px bg-white/10 flex-shrink-0" />

              {/* Right 30% — 3 widgets stacked */}
              <div className="flex-[30] min-w-0 flex flex-col gap-2">
                {/* Technical Analysis */}
                <div className="flex-1 min-h-0 flex flex-col">
                  <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mb-1">
                    Technical Analysis
                  </p>
                  <div className="flex-1 min-h-0">
                    <div ref={technicalRef} className="tradingview-widget-container w-full h-full">
                      <div className="tradingview-widget-container__widget w-full h-full" />
                    </div>
                  </div>
                </div>

                <div className="h-px bg-white/10 flex-shrink-0" />

                {/* Fundamental Data */}
                <div className="flex-1 min-h-0 flex flex-col">
                  <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mb-1">
                    Fundamental Data
                  </p>
                  <div className="flex-1 min-h-0">
                    <div ref={fundamentalRef} className="tradingview-widget-container w-full h-full">
                      <div className="tradingview-widget-container__widget w-full h-full" />
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}

export default TradingViewModal
